import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Search, Loader2, Send, CircleDollarSign, CalendarDays, BadgeCheck, FileDown, Package, Wallet, CheckCircle2, X, Mail } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { generateSupplierOrderPdf } from '../../lib/supplierOrderPdfGenerator';
import { resolveLogoUrl } from '../../lib/resolveLogoUrl';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { StatusTrackingTable, StatusTone } from '../ui/StatusTrackingTable';
import { StatusFilterChips } from '../ui/StatusFilterChips';
import { EmailHistoryModal } from '../ui/EmailHistoryModal';

interface OrderItem { label: string; qty: number; }

interface PendingEntry {
    document_request_id: string;
    template_name: string;
    contact_name: string;
    contact_email: string;
    amount_due: number;
    payment_status: string;
    sent_at: string;
    items: OrderItem[];
}

interface OrderEntry {
    order_id: string;
    document_request_id: string;
    status: 'a_commander' | 'commandee';
    ordered_at: string | null;
    created_at: string;
    desired_date: string;
    suggested_date: string;
    rdv_date: string;
    rdv_time: string;
    confirmed_at: string | null;
    template_name: string;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    amount_due: number;
    payment_status: string;
    paid_at: string | null;
    items: OrderItem[];
    email_count: number;
}

const fmtDate = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};
const fmtDateTime = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; }
};
const fmtMontant = (n?: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

const ItemsList: React.FC<{ items: OrderItem[] }> = ({ items }) => (
    <span className="text-xs text-content-muted">{items.map((it) => `${it.label} x${it.qty}`).join(' · ')}</span>
);

type Row = ({ kind: 'pending' } & PendingEntry) | ({ kind: 'order' } & OrderEntry);
type StatusValue = 'attente_paiement' | 'a_commander' | 'commandee' | 'confirmee';

const STATUS_TONE: Record<StatusValue, StatusTone> = {
    attente_paiement: { label: 'En attente de paiement', className: 'bg-amber-500/10 text-amber-500' },
    a_commander: { label: 'À commander', className: 'bg-surface-soft text-content-muted' },
    commandee: { label: 'Commandée', className: 'bg-green-500/10 text-green-500' },
    confirmee: { label: 'Confirmée', className: 'bg-brand-500/10 text-brand-600 dark:text-brand-400' },
};

const deriveStatus = (r: Row): StatusValue => r.kind === 'pending' ? 'attente_paiement' : r.status === 'a_commander' ? 'a_commander' : r.confirmed_at ? 'confirmee' : 'commandee';

/** Marque manuellement une démarche comme payée, avec un montant réglé saisi à la main — utile pour les
 *  démarches sans paiement en ligne configuré (choix fournisseur seuls, amount_due encore à 0). Passe
 *  automatiquement la démarche en "à commander", et envoie le mail au fournisseur si l'envoi automatique
 *  est activé sur la démarche — demande d'Emeni. */
const MarkPaidModal: React.FC<{ entry: PendingEntry; onClose: () => void; onPaid: () => void }> = ({ entry, onClose, onPaid }) => {
    const [amount, setAmount] = React.useState(entry.amount_due ? String(entry.amount_due) : '');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    const handleConfirm = async () => {
        setSaving(true);
        setError('');
        try {
            const res: any = await moduleApi.markDocumentRequestPaid(entry.document_request_id, Math.max(0, Number(amount.replace(',', '.')) || 0));
            if (res?.error) { setError(res.error); return; }
            onPaid();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la validation du paiement.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open onClose={onClose} title="Marquer comme payé" icon={<CheckCircle2 size={16} />} maxWidth="max-w-sm"
            footer={<>
                <Button variant="secondary" onClick={onClose}>Annuler</Button>
                <Button onClick={handleConfirm} disabled={saving}>
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    {saving ? 'Validation...' : 'Confirmer le paiement'}
                </Button>
            </>}>
            <div className="space-y-3 text-sm">
                <p className="text-content-muted">{entry.contact_name} — {entry.template_name}</p>
                <div>
                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Montant réglé</label>
                    <Input type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" autoFocus />
                </div>
                {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
            </div>
        </Modal>
    );
};

/** Détail complet d'une commande au clic sur sa ligne — paiement, envoi au fournisseur, réponse du
 *  fournisseur — toutes ces dates sont déjà renvoyées par l'API, seul l'affichage manquait (demande d'Emeni). */
const OrderDetailModal: React.FC<{ entry: Row; actions?: React.ReactNode; onClose: () => void }> = ({ entry, actions, onClose }) => {
    const isPaid = entry.payment_status === 'paid';
    return (
        <Modal open onClose={onClose} title={`${entry.contact_name} — ${entry.template_name}`} icon={<Package size={16} />} maxWidth="max-w-md"
            footer={<>{actions}<Button variant="secondary" onClick={onClose}>Fermer</Button></>}>
            <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                    <span className="text-content-muted flex items-center gap-1.5"><Wallet size={13} /> Paiement</span>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest ${isPaid ? 'bg-green-500/10 text-green-500' : 'bg-amber-500/10 text-amber-500'}`}>
                        {isPaid ? 'Payé' : 'Non payé'}
                    </span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-content-muted">Montant</span>
                    <span className="text-content font-bold">{fmtMontant(entry.amount_due)}</span>
                </div>
                {entry.kind === 'order' && entry.paid_at && (
                    <div className="flex items-center justify-between">
                        <span className="text-content-muted">Réglée le</span>
                        <span className="text-content">{fmtDateTime(entry.paid_at)}</span>
                    </div>
                )}
                {entry.kind === 'order' && entry.ordered_at && (
                    <div className="flex items-center justify-between">
                        <span className="text-content-muted flex items-center gap-1.5"><Send size={13} /> Envoyée au fournisseur le</span>
                        <span className="text-content">{fmtDateTime(entry.ordered_at)}</span>
                    </div>
                )}
                {entry.kind === 'order' && entry.confirmed_at && (
                    <div className="flex items-center justify-between">
                        <span className="text-content-muted flex items-center gap-1.5"><BadgeCheck size={13} /> Confirmée par le fournisseur le</span>
                        <span className="text-content">{fmtDateTime(entry.confirmed_at)}</span>
                    </div>
                )}
                {entry.kind === 'order' && entry.desired_date && (
                    <div className="flex items-center justify-between">
                        <span className="text-content-muted">Réception souhaitée</span>
                        <span className="text-content">{fmtDate(entry.desired_date)}</span>
                    </div>
                )}
                {entry.kind === 'pending' && entry.sent_at && (
                    <div className="flex items-center justify-between">
                        <span className="text-content-muted">Démarche envoyée le</span>
                        <span className="text-content">{fmtDateTime(entry.sent_at)}</span>
                    </div>
                )}
                <div className="pt-2 border-t border-border space-y-1.5">
                    <p className="text-xs font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><Package size={13} /> Articles</p>
                    {entry.items.map((it, idx) => (
                        <div key={idx} className="flex items-center justify-between px-2.5 py-2 rounded-md bg-surface-soft border border-border">
                            <span className="text-content">{it.label}</span>
                            <span className="text-content-muted">x{it.qty}</span>
                        </div>
                    ))}
                </div>
            </div>
        </Modal>
    );
};

export const SupplierOrdersPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [supplierName, setSupplierName] = React.useState('');
    const [supplierContact, setSupplierContact] = React.useState<{ phone: string; email: string; address: string; referentName: string; referentPhone: string; referentEmail: string }>({ phone: '', email: '', address: '', referentName: '', referentPhone: '', referentEmail: '' });
    const [pending, setPending] = React.useState<PendingEntry[]>([]);
    const [aCommander, setACommander] = React.useState<OrderEntry[]>([]);
    const [commandees, setCommandees] = React.useState<OrderEntry[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [q, setQ] = React.useState('');
    const [sendingId, setSendingId] = React.useState<string | null>(null);
    const [confirmingId, setConfirmingId] = React.useState<string | null>(null);
    const [cancelingId, setCancelingId] = React.useState<string | null>(null);
    const [error, setError] = React.useState('');
    // Champ "date de réception souhaitée" par commande — pré-rempli avec la date déjà choisie par le
    // client (RDV/événement lié) si l'API en suggère une, jamais imposé, modifiable avant l'envoi.
    const [dateByOrderId, setDateByOrderId] = React.useState<Record<string, string>>({});
    const [selectedEntry, setSelectedEntry] = React.useState<Row | null>(null);
    const [emailHistoryFor, setEmailHistoryFor] = React.useState<OrderEntry | null>(null);
    const [statusFilter, setStatusFilter] = React.useState<'all' | StatusValue>('all');
    const [payingEntry, setPayingEntry] = React.useState<PendingEntry | null>(null);

    const load = React.useCallback((query?: string) => {
        if (!id) return;
        setLoading(true);
        moduleApi.getSupplierOrders(id, query).then((d: any) => {
            setSupplierName(d?.supplier_name || '');
            setSupplierContact({
                phone: d?.supplier_phone || '', email: d?.supplier_email || '', address: d?.supplier_address || '',
                referentName: d?.supplier_referent_name || '', referentPhone: d?.supplier_referent_phone || '', referentEmail: d?.supplier_referent_email || '',
            });
            setPending(Array.isArray(d?.en_attente_paiement) ? d.en_attente_paiement : []);
            const toOrder: OrderEntry[] = Array.isArray(d?.a_commander) ? d.a_commander : [];
            setACommander(toOrder);
            setCommandees(Array.isArray(d?.commandees) ? d.commandees : []);
            setDateByOrderId((prev) => {
                const next = { ...prev };
                for (const o of toOrder) if (next[o.order_id] === undefined) next[o.order_id] = o.desired_date || o.suggested_date || '';
                return next;
            });
        }).catch((e: any) => setError(e?.message || 'Erreur chargement')).finally(() => setLoading(false));
    }, [id]);

    React.useEffect(() => { load(); }, [load]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        load(q.trim() || undefined);
    };

    const handleCancel = async (orderId: string, label: string) => {
        if (!id) return;
        if (!confirm(`Annuler la commande "${label}" ?`)) return;
        setCancelingId(orderId);
        setError('');
        try {
            const res: any = await moduleApi.cancelSupplierOrder(id, orderId);
            if (res?.error) { setError(res.error); return; }
            load(q.trim() || undefined);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'annulation.");
        } finally {
            setCancelingId(null);
        }
    };

    // Annule la démarche CRM entière (pas juste ce fournisseur) — décision confirmée avec Emeni, cette
    // liste étant reconstruite à partir de démarches pas encore réglées, potentiellement partagées entre
    // plusieurs fournisseurs.
    const handleCancelPending = async (p: PendingEntry) => {
        if (!confirm(`Annuler la démarche "${p.contact_name} — ${p.template_name}" ? Elle disparaîtra de toutes les files d'attente fournisseur concernées.`)) return;
        setCancelingId(p.document_request_id);
        setError('');
        try {
            const res: any = await moduleApi.cancelDocumentRequest(p.document_request_id);
            if (res?.error) { setError(res.error); return; }
            load(q.trim() || undefined);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'annulation.");
        } finally {
            setCancelingId(null);
        }
    };

    const handleSend = async (orderId: string) => {
        if (!id) return;
        setSendingId(orderId);
        setError('');
        try {
            await moduleApi.sendSupplierOrder(id, orderId, dateByOrderId[orderId] || '');
            load(q.trim() || undefined);
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'envoi de la commande.");
        } finally {
            setSendingId(null);
        }
    };

    const handleResend = async (orderId: string) => {
        if (!id) return;
        setSendingId(orderId);
        setError('');
        try {
            const res: any = await moduleApi.resendSupplierOrder(id, orderId);
            if (res?.error) { setError(res.error); return; }
            load(q.trim() || undefined);
        } catch (e: any) {
            setError(e?.message || "Erreur lors du renvoi de la commande.");
        } finally {
            setSendingId(null);
        }
    };

    const handleConfirmManually = async (orderId: string) => {
        if (!id) return;
        setConfirmingId(orderId);
        setError('');
        try {
            await moduleApi.confirmSupplierOrderManually(id, orderId);
            load(q.trim() || undefined);
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la confirmation.');
        } finally {
            setConfirmingId(null);
        }
    };

    const handleDownloadPdf = (o: OrderEntry) => {
        generateSupplierOrderPdf({
            orderId: o.order_id, supplierName, supplierPhone: supplierContact.phone, supplierEmail: supplierContact.email, supplierAddress: supplierContact.address,
            supplierReferentName: supplierContact.referentName, supplierReferentPhone: supplierContact.referentPhone, supplierReferentEmail: supplierContact.referentEmail,
            contactName: o.contact_name, contactPhone: o.contact_phone, contactEmail: o.contact_email, templateName: o.template_name,
            rdvDate: o.rdv_date, rdvTime: o.rdv_time, items: o.items,
            issuerName: user?.company_name || undefined, logoUrl: user?.logo_url ? resolveLogoUrl(user.logo_url) : null, brandColor: user?.theme_color || undefined,
        });
    };

    const allRows: Row[] = React.useMemo(() => [
        ...pending.map((p): Row => ({ kind: 'pending', ...p })),
        ...aCommander.map((o): Row => ({ kind: 'order', ...o })),
        ...commandees.map((o): Row => ({ kind: 'order', ...o })),
    ], [pending, aCommander, commandees]);
    const filteredRows = React.useMemo(() => statusFilter === 'all' ? allRows : allRows.filter((r) => deriveStatus(r) === statusFilter), [allRows, statusFilter]);

    const commandeesEnAttente = commandees.filter((o) => !o.confirmed_at).length;
    const confirmeesCount = commandees.filter((o) => !!o.confirmed_at).length;
    const chipOptions = [
        { value: 'attente_paiement', label: 'En attente de paiement', count: pending.length },
        { value: 'a_commander', label: 'À commander', count: aCommander.length },
        { value: 'commandee', label: 'Commandée', count: commandeesEnAttente },
        { value: 'confirmee', label: 'Confirmée', count: confirmeesCount },
    ];

    // Réutilisée à la fois pour les actions en ligne du tableau et pour le pied de la modale de détail
    // (Emeni : "quand je clique sur une commande, il m'affiche toutes les infos + les boutons
    // disponibles") — `afterAction` ferme la modale une fois une action déclenchée depuis celle-ci.
    const renderRowActions = (r: Row, afterAction?: () => void) => {
        if (r.kind === 'pending') {
            return (
                <>
                    <Button size="sm" onClick={() => { setPayingEntry(r); afterAction?.(); }} disabled={cancelingId === r.document_request_id}>
                        <CheckCircle2 size={13} /> Payé
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => { handleCancelPending(r); afterAction?.(); }} disabled={cancelingId === r.document_request_id}>
                        {cancelingId === r.document_request_id ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                    </Button>
                </>
            );
        }
        const status = deriveStatus(r);
        const label = `${r.contact_name} — ${r.template_name}`;
        return (
            <>
                {status === 'a_commander' && (
                    <>
                        <Button size="sm" onClick={() => { handleSend(r.order_id); afterAction?.(); }} disabled={sendingId === r.order_id || cancelingId === r.order_id}>
                            {sendingId === r.order_id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                            Commander
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => { handleCancel(r.order_id, label); afterAction?.(); }} disabled={sendingId === r.order_id || cancelingId === r.order_id}>
                            {cancelingId === r.order_id ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                        </Button>
                    </>
                )}
                {status === 'commandee' && (
                    <>
                        <Button size="sm" variant="secondary" onClick={() => { handleResend(r.order_id); afterAction?.(); }} disabled={sendingId === r.order_id || confirmingId === r.order_id || cancelingId === r.order_id}>
                            {sendingId === r.order_id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                            Relancer
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => { handleConfirmManually(r.order_id); afterAction?.(); }} disabled={confirmingId === r.order_id || cancelingId === r.order_id}>
                            {confirmingId === r.order_id ? <Loader2 size={13} className="animate-spin" /> : <BadgeCheck size={13} />}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => { handleCancel(r.order_id, label); afterAction?.(); }} disabled={confirmingId === r.order_id || cancelingId === r.order_id}>
                            {cancelingId === r.order_id ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                        </Button>
                    </>
                )}
                {status === 'confirmee' && (
                    <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); handleDownloadPdf(r); afterAction?.(); }}>
                        <FileDown size={13} /> PDF
                    </Button>
                )}
                <Button size="sm" variant="secondary" onClick={() => { setEmailHistoryFor(r); afterAction?.(); }} title="Historique des mails">
                    <Mail size={13} />
                </Button>
            </>
        );
    };

    return (
        <div className="space-y-6">
            {/* Pas de largeur maximale : cette page présente une LISTE, pas un formulaire — elle gagne à occuper l'écran d'un ordinateur. Les écrans de réglages gardent leur plafond : un formulaire étalé sur 1900 px devient illisible. Demande d'Emeni (2026-08-25). */}
            <div>
                <button onClick={() => navigate(`/fournisseurs/${id}`)} className="flex items-center gap-1.5 text-sm text-content-muted hover:text-content font-medium mb-2">
                    <ArrowLeft size={15} /> {supplierName || 'Fiche fournisseur'}
                </button>
                <h1 className="text-xl font-bold text-content tracking-tight">Commandes {supplierName ? `— ${supplierName}` : ''}</h1>
            </div>

            <form onSubmit={handleSearch} className="flex items-center gap-2">
                <div className="relative flex-1 max-w-sm">
                    <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un contact, une démarche..." className="pl-8" />
                </div>
                <Button type="submit" variant="secondary" size="sm">Rechercher</Button>
            </form>

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            <StatusFilterChips options={chipOptions} value={statusFilter} onChange={(v) => setStatusFilter(v as any)} />

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>
            ) : (
                <StatusTrackingTable<Row>
                    rows={filteredRows}
                    getRowKey={(r) => r.kind === 'pending' ? `pending-${r.document_request_id}` : `order-${r.order_id}`}
                    onRowClick={(r) => setSelectedEntry(r)}
                    getStatus={(r) => STATUS_TONE[deriveStatus(r)]}
                    emptyMessage="Aucune commande pour l'instant."
                    columns={[
                        { key: 'contact', header: 'Contact / Démarche', render: (r) => (<><span className="font-bold">{r.contact_name}</span> <span className="text-content-muted">— {r.template_name}</span></>) },
                        { key: 'articles', header: 'Articles', render: (r) => <ItemsList items={r.items} /> },
                        { key: 'montant', header: 'Montant', render: (r) => <span className="flex items-center gap-1"><CircleDollarSign size={12} className="text-content-muted" /> {fmtMontant(r.amount_due)}</span> },
                        { key: 'reception', header: 'Réception souhaitée', render: (r) => {
                            if (r.kind === 'order' && r.status === 'a_commander') {
                                return (
                                    <span onClick={(e) => e.stopPropagation()}>
                                        <input type="date" value={dateByOrderId[r.order_id] || ''}
                                            onChange={(e) => setDateByOrderId((prev) => ({ ...prev, [r.order_id]: e.target.value }))}
                                            className="bg-surface-soft border border-border rounded-md px-2 py-1 text-xs text-content outline-none focus:border-brand-500" />
                                    </span>
                                );
                            }
                            if (r.kind === 'order' && r.desired_date) return <span className="text-content-muted flex items-center gap-1"><CalendarDays size={11} /> {fmtDate(r.desired_date)}</span>;
                            return <span className="text-content-muted">—</span>;
                        } },
                        { key: 'derniere_action', header: 'Dernière action', render: (r) => {
                            if (r.kind === 'pending') return <span className="text-content-muted">—</span>;
                            if (r.confirmed_at) return <span className="text-content-muted">Confirmée le {fmtDate(r.confirmed_at)}</span>;
                            if (r.ordered_at) return <span className="text-content-muted">{r.email_count > 1 ? 'Relancée' : 'Commandée'} le {fmtDate(r.ordered_at)}</span>;
                            return <span className="text-content-muted">—</span>;
                        } },
                    ]}
                    renderActions={(r) => renderRowActions(r)}
                />
            )}

            {selectedEntry && (
                <OrderDetailModal entry={selectedEntry} onClose={() => setSelectedEntry(null)}
                    actions={renderRowActions(selectedEntry, () => setSelectedEntry(null))} />
            )}
            {payingEntry && (
                <MarkPaidModal entry={payingEntry} onClose={() => setPayingEntry(null)}
                    onPaid={() => { setPayingEntry(null); load(q.trim() || undefined); }} />
            )}
            {emailHistoryFor && id && (
                <EmailHistoryModal
                    title={`Historique des mails — ${emailHistoryFor.contact_name}`}
                    fetchHistory={() => moduleApi.getSupplierOrderEmailHistory(id, emailHistoryFor.order_id).then((res: any) => Array.isArray(res?.emails) ? res.emails : [])}
                    onClose={() => setEmailHistoryFor(null)}
                />
            )}
        </div>
    );
};
